/**
 * Leaderboard Component
 * 
 * Displays a simplified leaderboard showing users with the highest points.
 * Each correct answer (author, publication year, genre, age) earns one point.
 */

import React, { useState, useEffect } from 'react';
import { getLeaderboard } from '../../firebase/gameService';
import './Leaderboard.css';

/**
 * Leaderboard Component
 * 
 * @returns {JSX.Element} Simplified leaderboard component
 */
function Leaderboard() {
  // State variables
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  /**
   * Fetch leaderboard data from Firebase
   */
  useEffect(() => {
    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch points leaderboard - top 20 players
        const pointsData = await getLeaderboard(20);
        setLeaderboard(pointsData || []);

        // Debug output
        console.log("Fetched leaderboard data:", pointsData);
      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError('Failed to load leaderboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaderboardData();
  }, []);
  
  // Loading state
  if (loading) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-loading">
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-error">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-content-wrapper ios-fade-in">
        <div className="leaderboard-header">
          <h1>Leaderboard</h1>
          <p>One point for each correct answer about an acquaintance!</p>
        </div>
        
        <div className="leaderboard-content">
          <div className="leaderboard-points">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="rank-header">Rank</th>
                  <th className="player-header">Player</th>
                  <th className="points-header">Points</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard && leaderboard.length > 0 ? (
                  leaderboard.map((player, index) => (
                    <tr key={player.id} className={index < 3 ? `top-${index + 1}` : ''}>
                      <td className="rank-cell">
                        {index + 1}
                        {index < 3 && <span className="rank-medal">🏆</span>}
                      </td>
                      <td className="player-cell">
                        <div className="player-info">
                          {player.photoURL ? (
                            <img src={player.photoURL} alt={player.displayName} className="player-avatar" />
                          ) : (
                            <div className="avatar-placeholder">
                              {player.displayName ? player.displayName.charAt(0).toUpperCase() : 'U'}
                            </div>
                          )}
                          <span className="player-name">{player.displayName || "Anonymous Player"}</span>
                        </div>
                      </td>
                      <td className="points-cell">
                        <div className="points-container">
                          <span className="points-value">{player.totalPoints.toLocaleString()}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="empty-leaderboard">
                      No players yet! Be the first to score points!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard; 